// /redeem/<code> — direct link from a coach to a student.
//
// The code arrives via the URL path segment. RedeemFlow then renders
// WITHOUT the code-input field so the student doesn't see or touch the
// value.
//
// Code-reading strategy (belt-and-suspenders, per the 2026-06-21 bug
// where the previous parent-child layout swallowed the param):
//   1. PRIMARY: Route.useParams() from TanStack. Now that the parent
//      route renders <Outlet />, this component actually mounts and the
//      hook returns {code: "..."}.
//   2. FALLBACK: extract from window.location.pathname directly. Kicks
//      in only on the client and only if (1) returned empty — which
//      should never happen now, but if a future TanStack upgrade
//      regresses param hydration, the form still works.
//
// SSR-side fallback is N/A (no window) — but SSR-side primary is fine
// because the child mounts inside the parent's Outlet.

import { createFileRoute } from "@tanstack/react-router";

import { RedeemFlow } from "@/components/redeem/RedeemFlow";

export const Route = createFileRoute("/redeem/$code")({
  component: RedeemCodePage,
});

function readCodeFromUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const match = window.location.pathname.match(/\/redeem\/([^/?#]+)/);
  return match?.[1];
}

function RedeemCodePage() {
  // Primary: TanStack params. Cast is defensive against typegen lag.
  const params = Route.useParams() as { code?: string };
  // Fallback: re-read from the URL bar directly. If TanStack returns
  // empty for any reason (regression, stale routeTree, weird race),
  // the URL bar is still authoritative on the client.
  const code = params.code ?? readCodeFromUrl();
  return <RedeemFlow urlCode={code} />;
}
